import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { isFulfilled, nanoid } from "@reduxjs/toolkit";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import formatCurrency from "../../../common/helpers/formatCurrency";
import { leadingZerosDateFormat } from "../../../common/helpers/formatDateTime";
import formatNumber from "../../../common/helpers/formatNumber";
import InvoiceVatLabelString from "../common/InvoiceVatLabelString";
import InvoiceVatValueString from "../common/InvoiceVatValueString";
import { BANK_NAMES } from "../constants";
import {
  EezyCostCenter,
  InvoiceEdit,
  InvoiceRow,
  PaymentTerm,
} from "../invoiceAPI";
import {
  fetchCustomerOrgById,
  fetchEezyCompanies,
  fetchPaymentTerms,
} from "../invoiceDetailedSlice";
import { InvoiceContainer, InvoicePreviewItemGrid } from "../styled";
import {
  calculateInvoiceRowVats,
  getInvoiceOriginCode,
  isVatRatePercentZero,
} from "../utils";
import PreviewInvoiceBreakDownRows from "./PreviewInvoiceBreakDownRows";
import { CustomerCostCenter } from "../../customer/customerAPI";
import { PreviewAttachments } from "./PreviewAttachments";

interface Props {
  invoice: InvoiceEdit;
  isRowSelectionEnabled: boolean;
  onRowSelect: (rowId: string) => void;
}

export const PreviewInvoiceContent = ({
  invoice,
  isRowSelectionEnabled,
  onRowSelect,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation("invoices");
  const { seller, buyer, invoiceRows: rows } = invoice;
  const vatSums = calculateInvoiceRowVats(rows);
  const [paymentTerm, setPaymentTerm] = useState<PaymentTerm>();
  const [customerCCOptions, setCustomerCCOptions] = useState<
    CustomerCostCenter[]
  >([]);
  const [eezyBuyerCCs, setEezyBuyerCCs] = useState<EezyCostCenter[]>([]);
  const [paymentTermOptions, setPaymentTermOptions] = useState<PaymentTerm[]>(
    [],
  );

  const borderStyle = "1px solid black";
  const handleRowClick = (row: InvoiceRow) => {
    if (!isRowSelectionEnabled) return;
    onRowSelect(row.id);
  };

  const isFactoringProvider = !!invoice.factoringProvider;

  const iban = isFactoringProvider
    ? invoice.factoringProvider?.iban
    : seller?.bank.iban;
  const bic = isFactoringProvider
    ? invoice.factoringProvider?.bic
    : seller?.bank.bic;
  const bank = BANK_NAMES.find((bank) => bank.bic === bic);
  const bankName = i18n.language === "fi" ? bank?.nameFi : bank?.nameEn;
  const receiverName = isFactoringProvider
    ? invoice.factoringProvider?.name
    : seller?.name;

  useEffect(() => {
    dispatch(fetchPaymentTerms({})).then((action) => {
      if (isFulfilled(action)) {
        setPaymentTermOptions(action.payload.paymentTerms);
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (invoice) {
      const paymentTerm = paymentTermOptions.find(
        (term) => term.days === parseInt(invoice.paymentTermDays),
      );
      setPaymentTerm(paymentTerm);
    }
  }, [invoice, paymentTermOptions]);

  const originCode = getInvoiceOriginCode(invoice);
  const header = t(originCode);

  useEffect(() => {
    if (invoice.customerOrganizationId) {
      dispatch(
        fetchCustomerOrgById({ id: invoice.customerOrganizationId }),
      ).then((action) => {
        if (isFulfilled(action)) {
          setCustomerCCOptions(action.payload.costCenters || []);
        }
      });
    } else {
      dispatch(fetchEezyCompanies({})).then((action) => {
        if (isFulfilled(action)) {
          const eezyBuyer = action.payload.find(
            (company) => company.name === invoice.eezyBuyerCompanyName,
          );
          setEezyBuyerCCs(eezyBuyer?.costCenters || []);
        }
      });
    }
  }, [invoice, dispatch]);

  return (
    <InvoiceContainer>
      <PreviewAttachments invoice={invoice} />
      <Box>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Grid item container xs={12}>
              <Grid item xs={9}>
                <Box
                  component="img"
                  sx={{
                    height: 64,
                    width: 64,
                  }}
                  alt="Eezy logo"
                  src="/assets/eezy_round_logo.svg"
                />
              </Grid>
              <Grid item xs={3}>
                <Typography variant="header" fontWeight={700} fontSize={32}>
                  {header}
                </Typography>
                <br />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={9}>
            <Grid item container spacing={0} xs={12}>
              <Grid item xs={12}>
                {seller?.name}
              </Grid>
              <Grid item xs={12}>
                {seller?.address.street}
              </Grid>
              <Grid item xs={12}>
                {seller?.address.zip} {seller?.address.city}
              </Grid>
            </Grid>
            <br />
            <Grid
              item
              container
              spacing={0}
              xs={12}
              sx={{ padding: "30px 0px 80px 80px" }}
            >
              <Grid item xs={12}>
                <Typography variant="header" fontWeight={700} fontSize={18}>
                  {buyer?.name?.toUpperCase()}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="header" fontWeight={700} fontSize={18}>
                  {invoice.buyerContactPersonName?.toUpperCase()}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="header" fontWeight={700} fontSize={18}>
                  {buyer?.address.street?.toUpperCase()}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="header" fontWeight={700} fontSize={18}>
                  {buyer?.address.zip} {buyer?.address.city.toUpperCase()}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={3}>
            <Grid item container spacing={2} xs={12}></Grid>
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography fontWeight={600}>{`${t("date")}:`}</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>
                  {format(
                    invoice.invoiceDate
                      ? new Date(invoice.invoiceDate)
                      : new Date(),
                    leadingZerosDateFormat,
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography fontWeight={600}>{`${t("dueDate")}:`}</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>
                  {format(
                    new Date(invoice.invoiceDueDate),
                    leadingZerosDateFormat,
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography fontWeight={600}>{`${t("valueDate")}:`}</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>
                  {format(
                    new Date(invoice.invoiceValueDate),
                    leadingZerosDateFormat,
                  )}
                </Typography>
              </Grid>
            </Grid>
            {invoice.deliveryDate && (
              <Grid item container spacing={2} xs={12}>
                <Grid item xs={4}>
                  <Typography fontWeight={600}>
                    {`${t("deliveryDate")}:`}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography>
                    {format(
                      new Date(invoice.deliveryDate),
                      leadingZerosDateFormat,
                    )}
                  </Typography>
                </Grid>
              </Grid>
            )}
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography fontWeight={600}>
                  {`${t("invoiceNumber")}:`}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>{invoice.invoiceNumber}</Typography>
              </Grid>
            </Grid>
            {!!invoice.originalInvoiceNumber && (
              <Grid item container spacing={2} xs={12}>
                <Grid item xs={4}>
                  <Typography fontWeight={600}>
                    {`${t("originalInvoiceNumber")}:`}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography>{invoice.originalInvoiceNumber}</Typography>
                </Grid>
              </Grid>
            )}
            {!!invoice.salesPersonName && (
              <Grid item container spacing={2} xs={12}>
                <Grid item xs={4}>
                  <Typography fontWeight={600}>
                    {`${t("contactPerson")}:`}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography>{invoice.salesPersonName}</Typography>
                </Grid>
              </Grid>
            )}
            <br />
            {!!buyer?.sellersBuyerIdentifier && (
              <Grid item container spacing={2} xs={12}>
                <Grid item xs={4}>
                  <Typography fontWeight={600}>
                    {`${t("customerNumber")}:`}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography>{buyer.sellersBuyerIdentifier}</Typography>
                </Grid>
              </Grid>
            )}
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography fontWeight={600}>
                  {`${t("businessId")}:`}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>{buyer?.businessId}</Typography>
              </Grid>
            </Grid>
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography fontWeight={600}>{`${t("taxCode")}:`}</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>{buyer?.taxCode}</Typography>
              </Grid>
            </Grid>
            <br />
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography
                  fontWeight={600}
                  hidden={!invoice.sellerReferenceIdentifier}
                >
                  {`${t("sellerReference")}:`}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography hidden={!invoice.sellerReferenceIdentifier}>
                  {invoice.sellerReferenceIdentifier}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography
                  fontWeight={600}
                  hidden={!invoice.buyerReferenceIdentifier}
                >
                  {`${t("buyerReference")}:`}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography hidden={!invoice.buyerReferenceIdentifier}>
                  {invoice.buyerReferenceIdentifier}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography fontWeight={600} hidden={!invoice.orderIdentifier}>
                  {`${t("orderReference")}:`}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography hidden={!invoice.orderIdentifier}>
                  {invoice.orderIdentifier}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography
                  fontWeight={600}
                  hidden={!invoice.agreementIdentifier}
                >
                  {`${t("agreementReference")}:`}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography hidden={!invoice.agreementIdentifier}>
                  {invoice.agreementIdentifier}
                </Typography>
              </Grid>
            </Grid>
            <br />
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography
                  fontWeight={600}
                  hidden={!invoice.factoringProvider?.name}
                >
                  {`${t("factoringFinanceCompanyName")}:`}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography hidden={!invoice.factoringProvider?.name}>
                  {invoice.factoringProvider?.name}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography
                  fontWeight={600}
                  hidden={!invoice.factoringProvider?.businessId}
                >
                  {`${t("factoringFinanceCompanyBusinessId")}:`}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography hidden={!invoice.factoringProvider?.businessId}>
                  {invoice.factoringProvider?.businessId}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography
                  fontWeight={600}
                  hidden={!invoice.factoringProvider?.transferClause}
                >
                  {`${t("factoringTransferClause")}:`}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container spacing={2} xs={12}>
              <Grid item>
                <Typography hidden={!invoice.factoringProvider?.transferClause}>
                  {invoice.factoringProvider?.transferClause.join(" ")}
                </Typography>
              </Grid>
            </Grid>
            <br />
          </Grid>
        </Grid>
      </Box>
      {invoice.freeText && <Typography>{invoice.freeText}</Typography>}
      <Table size="small" sx={{ borderTop: borderStyle, marginBottom: "25px" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>{t("articleName")}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t("articleCategory")}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t("delivered")}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t("unitPrice")}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t("taxFree")}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t("vatAmount")}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t("sum")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoice.invoiceRows?.map((row) => (
            <TableRow
              key={row.id}
              hover={isRowSelectionEnabled}
              onClick={() => handleRowClick(row)}
              sx={{ cursor: isRowSelectionEnabled ? "pointer" : "default" }}
            >
              <TableCell>
                {row.articleName} <br /> {row.freeText}
              </TableCell>
              <TableCell sx={{ whiteSpace: "pre-line" }}>
                {row.articleCategory}
                {row.editable && (
                  <>
                    <br />
                    {row.customerCostCenterId
                      ? customerCCOptions.find(
                          (cc) => cc.id === row.customerCostCenterId,
                        )?.name
                      : eezyBuyerCCs.find(
                          (cc) => cc.eezyCostCenterId === row.eezyCostCenterId,
                        )?.name}
                  </>
                )}
              </TableCell>
              <TableCell>
                {formatNumber(row.quantityAmount)} {row.quantityUnit}
              </TableCell>
              <TableCell>{formatCurrency(row.unitPriceAmount)}</TableCell>
              <TableCell>{formatCurrency(row.vatExcludedAmount)}</TableCell>
              <TableCell>
                {`${formatCurrency(row.vatAmount)} (${formatNumber(
                  row.vatRatePercent,
                )}%)`}
                {isVatRatePercentZero(row.vatRatePercent) && (
                  <Typography variant="body2">{row.vatCode}</Typography>
                )}
              </TableCell>
              <TableCell>{formatCurrency(row.amount)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Grid
        container
        spacing={2}
        sx={{ borderTop: borderStyle, marginBottom: "25px" }}
      >
        <Grid item xs={8}>
          <Grid item container xs={12}>
            <Grid item xs={2}>
              <Typography fontWeight={600}>{`${t(
                "delayInformation",
              )}:`}</Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography>{paymentTerm?.description}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item xs={2}>
              <Typography fontWeight={600}>{`${t(
                "penaltyInterest",
              )}:`}</Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography>
                {`${formatNumber(paymentTerm?.interestRate)} %`}
              </Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item xs={2}>
              <Typography fontWeight={600}>{`${t("paymentTerm")}:`}</Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography>
                {invoice.paymentTermDays} {t("days", { ns: "common" })}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4}>
          <Grid item container xs={12}>
            <Grid item xs={4}>
              <Typography fontWeight={600}>{`${t("taxFree")}:`}</Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography>
                {formatCurrency(invoice.vatExcludedAmount)}
              </Typography>
            </Grid>
          </Grid>
          {vatSums.map((vatSum) => (
            <Grid key={nanoid()} item container xs={12}>
              <React.Fragment
                key={`${vatSum.vatRatePercent}_${vatSum.vatCode}`}
              >
                <Grid item xs={4}>
                  <Typography fontWeight={600}>
                    <InvoiceVatLabelString vatSum={vatSum} />
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography>
                    <InvoiceVatValueString vatSum={vatSum} />
                  </Typography>
                </Grid>
                {isVatRatePercentZero(vatSum.vatRatePercent) && (
                  <Grid item>
                    <Typography>{vatSum.vatFreeText}</Typography>
                  </Grid>
                )}
              </React.Fragment>
            </Grid>
          ))}
          <Grid item container xs={12}>
            <Grid item xs={4}>
              <Typography fontWeight={600}>{`${t("sum")}:`}</Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography>
                {formatCurrency(invoice.vatIncludedAmount)}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <PreviewInvoiceBreakDownRows rows={invoice.breakDownRows} />
      <Grid container spacing={2} sx={{ borderTop: borderStyle }}>
        <Grid item xs={8}>
          <Grid item container xs={12}>
            {seller?.name}
          </Grid>
          <Grid item container xs={12}>
            {seller?.webAddress}
          </Grid>
          <Grid item container xs={12}>
            {seller?.emailAddress}
          </Grid>
        </Grid>
        <Grid item xs={4}>
          <Grid item container xs={12}>
            <Grid item xs={4}>
              <Typography fontWeight={600}>{`${t("businessId")}:`}</Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography>{seller?.businessId}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item xs={4}>
              <Typography fontWeight={600}>{`${t("taxCode")}:`}</Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography>{seller?.taxCode}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item xs={4}>
              <Typography fontWeight={600}>{`${bankName}:`}</Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography>{`${iban} / ${bic}`}</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <br />
      <br />
      <Grid container>
        <Grid item xs={8} sx={{ border: borderStyle, borderLeft: "none" }}>
          <Grid item container xs={12}>
            <InvoicePreviewItemGrid
              item
              xs={2}
              sx={{
                borderRight: borderStyle,
                borderBottom: borderStyle,
              }}
            >
              <Typography>{t("receiverBank")}</Typography>
            </InvoicePreviewItemGrid>
            <InvoicePreviewItemGrid
              item
              xs={10}
              sx={{
                borderBottom: borderStyle,
              }}
            >
              <Typography>{bankName}</Typography>
            </InvoicePreviewItemGrid>
          </Grid>
          <Grid item container xs={12}>
            <InvoicePreviewItemGrid
              item
              xs={2}
              sx={{
                borderRight: borderStyle,
                borderBottom: borderStyle,
              }}
            >
              <Typography>{t("receiver")}</Typography>
            </InvoicePreviewItemGrid>
            <InvoicePreviewItemGrid
              item
              xs={10}
              sx={{ borderBottom: borderStyle }}
            >
              <Typography>{receiverName}</Typography>
            </InvoicePreviewItemGrid>
          </Grid>
          <Grid item container xs={12}>
            <InvoicePreviewItemGrid
              item
              xs={2}
              sx={{ borderRight: borderStyle }}
            >
              <Typography>{t("payer")}</Typography>
            </InvoicePreviewItemGrid>
            <InvoicePreviewItemGrid
              item
              xs={10}
              sx={{ borderBottom: "1px solid #F7F7F7" }}
            >
              <Typography>
                {buyer?.name}
                <br />
                {buyer?.address?.street}
                <br />
                {buyer?.address.zip} {buyer?.address.city}
              </Typography>
            </InvoicePreviewItemGrid>
            <InvoicePreviewItemGrid
              item
              xs={2}
              sx={{
                borderRight: borderStyle,
                borderBottom: borderStyle,
              }}
            >
              <Typography>{t("signature")}</Typography>
            </InvoicePreviewItemGrid>
            <InvoicePreviewItemGrid
              item
              xs={10}
              sx={{ borderBottom: borderStyle }}
            >
              <>
                <hr
                  style={{
                    padding: "0px",
                    marginTop: "20px",
                    marginBottom: "0px",
                  }}
                />
              </>
            </InvoicePreviewItemGrid>
          </Grid>
          <Grid item container xs={12}>
            <InvoicePreviewItemGrid
              item
              xs={2}
              sx={{
                borderRight: borderStyle,
              }}
            >
              <Typography>&nbsp;</Typography>
            </InvoicePreviewItemGrid>
            <InvoicePreviewItemGrid item xs={10}>
              <Typography>&nbsp;</Typography>
            </InvoicePreviewItemGrid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={4}
          sx={{
            border: borderStyle,
            borderRight: "none",
            borderLeft: "none",
          }}
        >
          <Grid item container xs={12}>
            <Grid
              item
              xs={6}
              sx={{
                borderRight: borderStyle,
                borderBottom: borderStyle,
                height: "49px",
                padding: "0px 10px ",
              }}
            >
              <Typography>
                {t("iban")} <br /> {iban}
              </Typography>
            </Grid>
            <Grid
              item
              xs={6}
              sx={{
                borderBottom: borderStyle,
                height: "49px",
                padding: "0px 10px ",
              }}
            >
              <Typography>
                {t("bic")} <br />
                {bic}
              </Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <InvoicePreviewItemGrid item xs={6}>
              <Typography>
                &nbsp;
                <br />
                &nbsp;
                <br />
                &nbsp;
                <br />
              </Typography>
            </InvoicePreviewItemGrid>
            <InvoicePreviewItemGrid item xs={6}>
              <Typography>&nbsp;</Typography>
            </InvoicePreviewItemGrid>
          </Grid>
          <Grid item container xs={12} sx={{ borderBottom: borderStyle }}>
            <InvoicePreviewItemGrid item xs={6}>
              <Typography>&nbsp;</Typography>
            </InvoicePreviewItemGrid>
            <InvoicePreviewItemGrid
              item
              xs={6}
              sx={{ borderTop: "1px solid #F7F7F7" }}
            >
              <Typography>&nbsp;</Typography>
            </InvoicePreviewItemGrid>
          </Grid>
          <Grid item container xs={12}>
            <InvoicePreviewItemGrid
              item
              xs={3}
              sx={{
                borderRight: borderStyle,
              }}
            >
              <Typography>{t("referenceNumber")}</Typography>
            </InvoicePreviewItemGrid>
            <InvoicePreviewItemGrid item xs={9}>
              <Typography>{invoice.referenceNumber}</Typography>
            </InvoicePreviewItemGrid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item container xs={6}>
              <InvoicePreviewItemGrid
                item
                xs={6}
                sx={{
                  borderTop: borderStyle,
                  borderRight: borderStyle,
                }}
              >
                <Typography>{t("dueDate")}</Typography>
              </InvoicePreviewItemGrid>
              <InvoicePreviewItemGrid
                item
                xs={6}
                sx={{
                  borderTop: borderStyle,
                  borderRight: borderStyle,
                }}
              >
                <Typography>
                  {format(
                    new Date(invoice.invoiceDueDate),
                    leadingZerosDateFormat,
                  )}
                </Typography>
              </InvoicePreviewItemGrid>
            </Grid>
            <Grid item container xs={6}>
              <InvoicePreviewItemGrid
                item
                xs={6}
                sx={{
                  borderTop: borderStyle,
                }}
              >
                &nbsp;
              </InvoicePreviewItemGrid>
              <InvoicePreviewItemGrid
                item
                xs={6}
                sx={{ borderTop: borderStyle }}
              >
                <Typography>
                  {formatCurrency(invoice.vatIncludedAmount)}
                </Typography>
              </InvoicePreviewItemGrid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </InvoiceContainer>
  );
};
